"use client";
import React, { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import Select from "react-select";
import { stateCityData } from "@/utils/stateCityData";
import { submitLeadMeritto } from "@/helpers/submitLeadMeritto";

const OTP_REGEX = /^\d{4}$/;
const emailRegex = /^[a-zA-Z0-9._%+-]{4,}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,7}$/;
const phoneRegex = /^(?:\+91|91)?[6-9]\d{9}$/;

const UserFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  shouldDownload,
  courseDetails,
  specialization,
  course,
  schoolName,
  stream,
  apiUrl,
}) => {
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const INITIAL_FORM_STATE = {
    name: "",
    email: "",
    phone: "",
    programme: "",
    Specialization: specialization,
    state: "",
    city: "",
    downloadBrochureStatus: false,
  };

  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [cities, setCities] = useState([]);

  const formattedStates = Object.keys(stateCityData).map((s) => ({
    value: s,
    label: s,
  }));

  const fetchCities = (stateName) => {
    const cityList = stateCityData[stateName] || [];
    setCities(cityList.map((c) => ({ value: c, label: c })));
  };

  // ── Resend countdown timer ──
  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  // ── Lock body scroll ──
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    setFormError("");
    setFormSuccess("");
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // ── Sync downloadBrochureStatus ──
  useEffect(() => {
    if (isOpen) {
      setForm((prev) => ({
        ...prev,
        downloadBrochureStatus: Boolean(shouldDownload),
      }));
    }
  }, [shouldDownload, isOpen]);

  // ── Reset OTP state on modal close ──
  useEffect(() => {
    if (!isOpen) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValue("");
      setFormError("");
      setResendTimer(0);
    }
  }, [isOpen]);

  // ── Load programme + specialization from props only ──
  useEffect(() => {
    if (!courseDetails) return;

    const programName = courseDetails.programDetails.program;

    setForm((prev) => ({
      ...prev,
      programme: programName,
      Specialization: specialization || "",
    }));
  }, [courseDetails, specialization]);

  // ── Handlers ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError("");
    setFormSuccess("");
    if (name === "state") {
      fetchCities(value);
      setForm((prev) => ({ ...prev, city: "" }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError("");
    setFormSuccess("");
    if (name === "state") {
      fetchCities(value);
      setForm((prev) => ({ ...prev, city: "" }));
    }
  };

  // ── Send OTP ──
  const handleSendOtp = async () => {
    if (!phoneRegex.test(form.phone)) {
      setFormError("Please enter a valid 10-digit mobile number");
      return;
    }
    setOtpLoading(true);
    setFormError("");
    try {
      const res = await fetch("https://lp.msu.edu.in/lead/api/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: `91${form.phone}` }),
      });
      const data = await res.json();
      if (!data.success) {
        setFormError(data.message);
        return;
      };
      setOtpSent(true);
      setResendTimer(30);
    } catch (err) {
      setFormError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Verify OTP ──
  const handleVerifyOtp = async () => {
    if (!OTP_REGEX.test(otpValue)) {
      setFormError("OTP must be exactly 4 digits.");
      return;
    }
    setOtpLoading(true);
    setFormError("");
    try {
      const res = await fetch("https://lp.msu.edu.in/lead/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: `91${form.phone}`, otp: otpValue }),
      });
      const data = await res.json();
      if (!data.success) {
        setFormError(data.message);
        return;
      };
      setOtpVerified(true);
      setFormError("");
    } catch (err) {
      setFormError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };


  // Reset Form
  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      programme: courseDetails?.programDetails?.program || "",
      Specialization: specialization || "",
      state: "",
      city: "",
      downloadBrochureStatus: false,
    });
  };
  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setFormError("");
    setFormSuccess("");

    const { name, email, phone, programme, Specialization, state, city } = form;

    if (!name.trim()) {
      setFormError("Please enter your name.");
      return;
    }

    if (!email.trim()) {
      setFormError("Please enter your email address.");
      return;
    }

    if (!emailRegex.test(email)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    if (!phone.trim()) {
      setFormError("Please enter your mobile number.");
      return;
    }

    if (!phoneRegex.test(phone)) {
      setFormError("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (!otpVerified) {
      setFormError("Please verify your mobile number via OTP.");
      return;
    }

    if (!programme) {
      setFormError("Please select a programme.");
      return;
    }

    // if (!Specialization) {
    //   setFormError("Please select a specialization.");
    //   return;
    // }

    if (!state) {
      setFormError("Please select your state.");
      return;
    }

    if (!city) {
      setFormError("Please select your city.");
      return;
    }

    setLoading(true);
    try {

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.phone,
        state: form.state,
        city:form.city,
        campus: schoolName,
        course: course,
        specialization: specialization,
        country_dial_code: "+91",

        source: 'direct',
        medium: 'direct',
        campaign: 'Co-Led',
      };

      await submitLeadMeritto(payload);

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();


      if (!data.success) throw new Error(data.message);

      setFormSuccess("Form submitted successfully!");
      // setForm(INITIAL_FORM_STATE);
      resetForm();
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValue("");
      setResendTimer(0);
      setFormError("");

      setTimeout(() => {
        onClose();
        onSuccess?.();
      }, 3000)

    } catch (error) {
      setFormError("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  // ── React Select styles ──
  const customStyles = {
    control: (base) => ({
      ...base,
      padding: "2px",
      borderColor: "#e5e7eb",
      borderRadius: "0.25rem",
      boxShadow: "none",
      "&:hover": { borderColor: "#d1d5db" },
    }),
    placeholder: (base) => ({ ...base, color: "#9ca3af" }),
    groupHeading: (base) => ({ ...base, color: "#1f2937", fontWeight: "bold" }),
  };

  const formattedCities = cities;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 flex items-center justify-center px-3">
      <div onClick={onClose} className="absolute inset-0 bg-black/60" />

      <div className="relative w-full max-w-md bg-white rounded-lg p-6 z-50">
        <X className="cursor-pointer absolute right-4 top-4" onClick={onClose} />

        <div className="text-xl font-semibold mb-5">Let's Get You Started!</div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Name */}
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Name"
            className="w-full border p-2 rounded"
          />

          {/* Email */}
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className="w-full border p-2 rounded"
          />

          {/* Phone + Send OTP */}
          <div className="flex gap-2">
            <input
              name="phone"
              value={form.phone}
              onChange={(e) => {
                handleChange(e);
                if (otpSent) {
                  setOtpSent(false);
                  setOtpVerified(false);
                  setOtpValue("");
                  setFormError("");
                }
              }}
              placeholder="Phone"
              className="flex-1 border p-2 rounded"
              disabled={otpVerified}
            />
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={otpLoading || otpVerified || resendTimer > 0}
              className={`px-3 py-2 rounded text-sm font-semibold whitespace-nowrap transition-colors
                ${otpVerified
                  ? "bg-green-100 text-green-700 border border-green-300 cursor-default"
                  : resendTimer > 0
                    ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    : "bg-[#F7A400] text-white cursor-pointer hover:bg-[#e09300]"
                }`}
            >
              {otpVerified
                ? "✓ Verified"
                : otpLoading
                  ? "Sending..."
                  : otpSent
                    ? resendTimer > 0
                      ? `Resend (${resendTimer}s)`
                      : "Resend OTP"
                    : "Send OTP"}
            </button>
          </div>

          {/* OTP Input + Verify */}
          {otpSent && !otpVerified && (
            <div className="space-y-1">
              <div className="flex gap-2">
                <input
                  value={otpValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setOtpValue(val);
                    setFormError("");
                  }}
                  placeholder="Enter 4-digit OTP"
                  maxLength={4}
                  inputMode="numeric"
                  // className={`flex-1 border p-2 rounded tracking-widest text-center font-mono text-lg ${otpError ? "border-red-400" : ""
                  //   }`}
                  className={`flex-1 border p-2 rounded tracking-widest text-center font-mono text-lg ${formError ? "border-red-400" : ""
                    }`}
                />
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || otpValue.length !== 4}
                  className={`px-4 py-1 rounded text-sm font-semibold transition-colors
                    ${otpValue.length === 4 && !otpLoading
                      ? "bg-[#F7A400] text-white cursor-pointer hover:bg-[#e09300]"
                      : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                >
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>

            </div>
          )}

          {/* Specialization — disabled, auto-filled from prop */}
          <input
            value={form.Specialization}
            disabled
            className="w-full border p-2 rounded bg-gray-100 text-gray-500 cursor-not-allowed"
          />

          {/* State */}
          <Select
            name="state"
            placeholder="Select State"
            className="w-full border rounded"
            options={formattedStates}
            onChange={(opt) => handleSelectChange("state", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.state
                ? formattedStates.find((s) => s.value === form.state)
                : null
            }
          />

          {/* City */}
          <Select
            name="city"
            placeholder="Select City"
            className="w-full border rounded"
            options={formattedCities}
            onChange={(opt) => handleSelectChange("city", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.city
                ? formattedCities.find((c) => c.value === form.city)
                : null
            }
          />

          {formError && (
            <p className="text-red-500 text-sm text-center">
              {formError}
            </p>
          )}

          {formSuccess && (
            <p className="text-[#0B202C] text-sm text-center">
              {formSuccess}
            </p>
          )}

          <button
            disabled={loading}
            className="w-full bg-[#F7A400] py-2 rounded font-semibold flex justify-center gap-2 cursor-pointer disabled:opacity-60"
          >
            {loading ? "Submitting..." : "SUBMIT"} <ArrowRight />
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;